import {
  Breadcrumbs,
  type Breadcrumb,
  AvatarWithName,
  mq,
  Text,
  Link,
  Container,
} from "@certn/cascadia-react";
import { useMediaQuery } from "@certn/cascadia-react/hooks/useMediaQuery";
import { breakpoints } from "@certn/cascadia-tokens";
import { Name } from "@certn/utils";
import { css } from "@emotion/css";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface ProfileTitleSubtitleProps {
  email?: string;
  lastOrder?: string | null;
}

function ProfileTitleSubtitle({ email, lastOrder }: ProfileTitleSubtitleProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.subtitleContainer}>
      <div>
        <Text
          style="global/body-secondary"
          color="text-weak"
          testID="profile-email-label"
        >
          {t("Email: ")}
        </Text>
        <Link
          textStyle="global/body-secondary-link"
          textColor="text-weak"
          testID="profile-email-address"
          href={`mailto:${email}`}
        >
          {email}
        </Link>
      </div>
      {lastOrder && (
        <Text
          style="global/body-secondary"
          color="text-weak"
          testID="profile-last-order"
        >
          {t(`  Last order: {{lastOrder}}`, { lastOrder })}
        </Text>
      )}
    </div>
  );
}

interface personProfileTitleCardProps {
  name?: Name;
  email?: string;
  lastOrder?: string | null;
  breadcrumbs?: Breadcrumb[];
  actions?: ReactNode;
}

export function PersonProfileTitleCard({
  name,
  email,
  lastOrder,
  breadcrumbs,
  actions,
}: personProfileTitleCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-sm"].maxWidth.toString()}px)`
  );

  const resolvedBreadcrumbs: Breadcrumb[] = breadcrumbs ?? [
    {
      navigate: () => navigate("/profiles"),
      title: t("Profiles"),
    },
    {
      title: t("Profile"),
    },
  ];

  return (
    <div className={styles.container}>
      <Breadcrumbs
        breadcrumbs={resolvedBreadcrumbs}
        testID="person-profiles-breadcrumbs"
      />
      <div className={styles.titleRow}>
        <div className={styles.nameRow}>
          <AvatarWithName
            name={name}
            nameAs="h1"
            subtitle={
              !isMobile && (
                <ProfileTitleSubtitle email={email} lastOrder={lastOrder} />
              )
            }
            textStyle={isMobile ? "global/title-small" : "global/title-medium"}
            size={isMobile ? "medium" : "x-large"}
            testID="person-profile"
          />
        </div>
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
      {isMobile && (
        <Container>
          <ProfileTitleSubtitle email={email} lastOrder={lastOrder} />
        </Container>
      )}
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--surface-base)",
    gap: "var(--space-4)",
    padding: "var(--space-4)",
  }),
  titleRow: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--space-3)",
  }),
  nameRow: css({
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
  }),
  actions: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "var(--space-3)",
  }),
  subtitleContainer: css(
    {
      display: "flex",
      flexWrap: "wrap",
      columnGap: "var(--space-4)",
    },
    mq({
      flexDirection: ["column", "row"],
      rowGap: ["var(--space-4)", 0],
    })
  ),
};
